// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Alert, Badge, Box, Button, Group, Modal, Select, Stack, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { formatDate, getExtension, getReferenceString } from '@medplum/core';
import type {
  Claim,
  ClaimResponse,
  Encounter,
  Organization,
  Patient,
  Practitioner,
  Reference,
  Task,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { DataTable } from 'mantine-datatable';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { CLAIM_PATIENT_PAID_URL } from '../../../bots/services/claims/constants';
import { TableAnchor } from '../layout/TableAnchor';
import classes from './ClaimsList.module.css';
import { CLAIM_TASK_SYSTEM, CLAIM_VALIDATION_TASK_CODE, isOpenClaimTask } from './claimTasks';

const PAGE_SIZE = 8;
const STEDI_SUBMITTED_AT_URL = 'http://polarissleep.com/fhir/extensions/stedi/submitted-at';
const CLAIM_PLACE_OF_SERVICE_URL = 'http://polarissleep.com/fhir/extensions/place-of-service';

interface EnrichedData {
  receivedAmount: Record<string, number>;
  patientAmt: Record<string, number>;
  payerOutcome: Record<string, string>;
  hasError: Set<string>;
  taskIssueCount: Record<string, number>;
  taskIssueSummary: Record<string, string>;
}

/** The rendering Practitioner: Claim.provider when it is one, else the first careTeam member (Claim.provider is the billing Organization). */
function getRenderingProviderReference(claim: Claim): string | undefined {
  const candidates = [claim.provider?.reference, claim.careTeam?.[0]?.provider?.reference];
  return candidates.find((reference) => reference?.startsWith('Practitioner/'));
}

function hasDraftValidationError(claim: Claim, encounters: Record<string, Encounter>): boolean {
  if (!claim.diagnosis?.length || !claim.item?.length) {
    return true;
  }
  const encounterReference = claim.item[0]?.encounter?.[0]?.reference;
  if (!encounterReference) {
    return true;
  }
  return encounters[encounterReference]?.status !== 'finished';
}

function getDraftValidationIssues(claim: Claim, encounters: Record<string, Encounter>): string[] {
  const issues: string[] = [];
  if (!claim.diagnosis?.length) {
    issues.push('At least one ICD-10 diagnosis is required.');
  }
  if (!claim.item?.length) {
    issues.push('At least one billable charge item is required.');
  }
  const encounterReference = claim.item?.[0]?.encounter?.[0]?.reference;
  if (!encounterReference) {
    issues.push('This claim must be linked to a finished encounter.');
  } else if (encounters[encounterReference]?.status !== 'finished') {
    issues.push('The linked encounter must be finished.');
  }
  return issues;
}

async function loadEnrichedData(
  medplum: ReturnType<typeof useMedplum>,
  claims: Claim[],
  encounters: Record<string, Encounter>
): Promise<EnrichedData> {
  const claimRefs = new Set(
    claims.filter((c): c is Claim & { id: string } => Boolean(c.id)).map((c) => getReferenceString(c))
  );
  const hasError = new Set<string>();
  const taskIssueCount: Record<string, number> = {};
  const taskIssueSummary: Record<string, string> = {};
  const receivedAmount: Record<string, number> = {};
  const patientAmt: Record<string, number> = {};
  const payerOutcome: Record<string, string> = {};
  for (const claim of claims) {
    if (claim.id && hasDraftValidationError(claim, encounters)) {
      const claimReference = getReferenceString(claim as Claim & { id: string });
      hasError.add(claimReference);
      const inferredIssues = getDraftValidationIssues(claim, encounters);
      taskIssueCount[claimReference] = inferredIssues.length;
      taskIssueSummary[claimReference] = inferredIssues.join('\n');
    }
  }
  if (claimRefs.size > 0) {
    const tasks = await medplum.searchResources('Task', {
      code: `${CLAIM_TASK_SYSTEM}|${CLAIM_VALIDATION_TASK_CODE}`,
      _count: '1000',
    });
    for (const task of tasks) {
      const ref = task.focus?.reference;
      if (ref && claimRefs.has(ref) && isOpenClaimTask(task)) {
        hasError.add(ref);
        taskIssueCount[ref] = Math.max(taskIssueCount[ref] ?? 0, task.input?.length ?? 1);
        const messages = (task.input ?? [])
          .map((input: NonNullable<Task['input']>[number]) => input.valueString)
          .filter(Boolean);
        if (messages.length > 0) {
          taskIssueSummary[ref] = messages.join('\n');
        }
      }
    }
  }
  for (const claim of claims) {
    if (!claim.id) {
      continue;
    }
    const claimRef = `Claim/${claim.id}`;
    const patientPaid = claim.extension?.find((extension) => extension.url === CLAIM_PATIENT_PAID_URL)?.valueMoney
      ?.value;
    if (typeof patientPaid === 'number' && Number.isFinite(patientPaid)) {
      patientAmt[claimRef] = patientPaid;
    }
    const responses = (await medplum.searchResources('ClaimResponse', { request: claimRef })) as ClaimResponse[];
    const response = responses[0];
    if (response?.outcome) {
      payerOutcome[claimRef] = response.outcome;
    }
    const received = response?.payment?.amount?.value;
    if (typeof received === 'number' && Number.isFinite(received)) {
      receivedAmount[claimRef] = received;
    }
    const responsePatientPaid = response?.item
      ?.flatMap((item) => item.adjudication ?? [])
      .filter((adjudication) => adjudication.category?.coding?.some((coding) => coding.code === 'PR'))
      .reduce((sum, adjudication) => sum + (adjudication.amount?.value ?? 0), 0);
    if (responsePatientPaid && responsePatientPaid > 0) {
      patientAmt[claimRef] = responsePatientPaid;
    }
  }
  return { receivedAmount, patientAmt, payerOutcome, hasError, taskIssueCount, taskIssueSummary };
}

export function ClaimsList(): JSX.Element {
  const medplum = useMedplum();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [patients, setPatients] = useState<Record<string, Patient>>({});
  const [encounters, setEncounters] = useState<Record<string, Encounter>>({});
  const [practitioners, setPractitioners] = useState<Record<string, Practitioner>>({});
  const [payers, setPayers] = useState<Record<string, Organization>>({});
  const [enriched, setEnriched] = useState<EnrichedData>({
    receivedAmount: {},
    patientAmt: {},
    payerOutcome: {},
    hasError: new Set(),
    taskIssueCount: {},
    taskIssueSummary: {},
  });
  const [loading, setLoading] = useState(true);
  const [acknowledgingClaim, setAcknowledgingClaim] = useState<Claim | undefined>();
  const [submittedOn, setSubmittedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [acknowledging, setAcknowledging] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'active'>('all');
  const [payerOutcomeFilter, setPayerOutcomeFilter] = useState<'all' | 'none' | 'complete' | 'error' | 'partial'>(
    'all'
  );
  const [openTaskFilter, setOpenTaskFilter] = useState<'all' | 'open' | 'none'>('all');
  const [patientFilter, setPatientFilter] = useState('');
  const [payerFilter, setPayerFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const [page, setPage] = useState(1);

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        _sort: '-_lastUpdated',
        _count: '200',
        use: 'claim',
      };
      const results = await medplum.searchResources('Claim', params);
      setClaims(results);

      const patientRefs = new Set<string>();
      const encounterRefs = new Set<string>();
      const practitionerRefs = new Set<string>();
      const payerRefs = new Set<string>();

      for (const claim of results) {
        if (claim.patient?.reference) {
          patientRefs.add(claim.patient.reference);
        }
        const encounterRef = claim.item?.[0]?.encounter?.[0]?.reference;
        if (encounterRef?.startsWith('Encounter/')) {
          encounterRefs.add(encounterRef);
        }
        const providerRef = getRenderingProviderReference(claim);
        if (providerRef) {
          practitionerRefs.add(providerRef);
        }
        const payerRef = claim.insurer?.reference;
        if (payerRef?.startsWith('Organization/')) {
          payerRefs.add(payerRef);
        }
      }

      const [patientEntries, encounterEntries, practitionerEntries, payerEntries] = await Promise.all([
        Promise.all(
          [...patientRefs].map(async (ref) => {
            try {
              const p = await medplum.readReference({ reference: ref } as Reference<Patient>);
              return [ref, p] as [string, Patient];
            } catch {
              return null;
            }
          })
        ),
        Promise.all(
          [...encounterRefs].map(async (ref) => {
            try {
              const encounter = await medplum.readReference({ reference: ref } as Reference<Encounter>);
              return [ref, encounter] as [string, Encounter];
            } catch {
              return null;
            }
          })
        ),
        Promise.all(
          [...practitionerRefs].map(async (ref) => {
            try {
              const practitioner = await medplum.readReference({ reference: ref } as Reference<Practitioner>);
              return [ref, practitioner] as [string, Practitioner];
            } catch {
              return null;
            }
          })
        ),
        Promise.all(
          [...payerRefs].map(async (ref) => {
            try {
              const payer = await medplum.readReference({ reference: ref } as Reference<Organization>);
              return [ref, payer] as [string, Organization];
            } catch {
              return null;
            }
          })
        ),
      ]);

      const patientMap: Record<string, Patient> = {};
      for (const entry of patientEntries) {
        if (entry) {
          patientMap[entry[0]] = entry[1];
        }
      }

      setPatients(patientMap);

      const encounterMap: Record<string, Encounter> = {};
      for (const entry of encounterEntries) {
        if (entry) {
          encounterMap[entry[0]] = entry[1];
        }
      }
      setEncounters(encounterMap);

      const practitionerMap: Record<string, Practitioner> = {};
      for (const entry of practitionerEntries) {
        if (entry) {
          practitionerMap[entry[0]] = entry[1];
        }
      }
      setPractitioners(practitionerMap);

      const payerMap: Record<string, Organization> = {};
      for (const entry of payerEntries) {
        if (entry) {
          payerMap[entry[0]] = entry[1];
        }
      }
      setPayers(payerMap);

      const data = await loadEnrichedData(medplum, results, encounterMap);
      setEnriched(data);
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  const acknowledgeClaim = useCallback(async (): Promise<void> => {
    const claim = acknowledgingClaim;
    if (!claim?.id) {
      return;
    }
    setAcknowledging(true);
    try {
      const updated = await medplum.updateResource<Claim>({
        ...claim,
        status: 'active',
        extension: [
          ...(claim.extension ?? []).filter((extension) => extension.url !== STEDI_SUBMITTED_AT_URL),
          { url: STEDI_SUBMITTED_AT_URL, valueDateTime: new Date(`${submittedOn}T12:00:00`).toISOString() },
        ],
      });
      setClaims((current) => current.map((currentClaim) => (currentClaim.id === updated.id ? updated : currentClaim)));
      setAcknowledgingClaim(undefined);
      showNotification({ color: 'green', message: `Claim ${updated.id ?? claim.id} marked submitted to Stedi` });
    } catch (error) {
      showNotification({ color: 'red', message: error instanceof Error ? error.message : 'Unable to update claim' });
    } finally {
      setAcknowledging(false);
    }
  }, [acknowledgingClaim, medplum, submittedOn]);

  useEffect(() => {
    fetchClaims().catch(console.error);
  }, [fetchClaims]);

  const getPatientName = (claim: Claim): string => {
    const ref = claim.patient?.reference;
    if (!ref) {
      return '—';
    }
    const patient = patients[ref];
    if (!patient?.name?.[0]) {
      return ref;
    }
    const name = patient.name[0];
    return [name.given?.join(' '), name.family].filter(Boolean).join(' ');
  };

  const getPatientFhirId = (claim: Claim): string => {
    return claim.patient?.reference?.split('/')[1] ?? '';
  };

  const getCpts = (claim: Claim): string => {
    const codes = (claim.item ?? [])
      .map((item) => item.productOrService?.coding?.[0]?.code)
      .filter((code): code is string => Boolean(code));
    return codes.length > 0 ? codes.join(', ') : '—';
  };

  const getClaimTotal = (claim: Claim): string => {
    if (claim.total?.value != null) {
      return `$${claim.total.value.toFixed(2)}`;
    }
    const total =
      claim.item?.reduce(
        (sum, item) => sum + (item.net?.value ?? (item.unitPrice?.value ?? 0) * (item.quantity?.value ?? 1)),
        0
      ) ?? 0;
    return total > 0 ? `$${total.toFixed(2)}` : '—';
  };

  const getEncounter = (claim: Claim): Encounter | undefined => {
    const reference = claim.item?.[0]?.encounter?.[0]?.reference;
    return reference ? encounters[reference] : undefined;
  };
  const getService = (claim: Claim): string => {
    const encounter = getEncounter(claim);
    return encounter?.type?.[0]?.text ?? encounter?.type?.[0]?.coding?.[0]?.display ?? '—';
  };
  const getPlaceOfService = (claim: Claim): string => getExtension(claim, CLAIM_PLACE_OF_SERVICE_URL)?.valueCode ?? '—';
  const getProviderName = (claim: Claim): string => {
    const reference = getRenderingProviderReference(claim);
    const practitioner = reference ? practitioners[reference] : undefined;
    const name = practitioner?.name?.[0];
    return name ? [name.given?.join(' '), name.family].filter(Boolean).join(' ') : '—';
  };
  const getPayerName = (claim: Claim): string => {
    const reference = claim.insurer?.reference;
    return (reference ? payers[reference]?.name : undefined) ?? '—';
  };
  const getPayerId = (claim: Claim): string => {
    const reference = claim.insurer?.reference;
    return (reference ? payers[reference]?.identifier?.[0]?.value : undefined) ?? '—';
  };

  const filteredClaims = claims.filter((claim) => {
    if (statusFilter !== 'all' && claim.status !== statusFilter) {
      return false;
    }
    const claimRef = claim.id ? getReferenceString(claim as Claim & { id: string }) : '';
    const hasOpenTask = enriched.hasError.has(claimRef);
    const payerOutcome = enriched.payerOutcome[claimRef];
    if ((openTaskFilter === 'open' && !hasOpenTask) || (openTaskFilter === 'none' && hasOpenTask)) {
      return false;
    }
    if (
      (payerOutcomeFilter === 'none' && payerOutcome) ||
      (payerOutcomeFilter !== 'all' && payerOutcomeFilter !== 'none' && payerOutcome !== payerOutcomeFilter)
    ) {
      return false;
    }
    const patient = getPatientName(claim).toLowerCase();
    const payer = `${getPayerName(claim)} ${getPayerId(claim)}`.toLowerCase();
    const provider = getProviderName(claim).toLowerCase();
    const search = searchFilter.toLowerCase();
    return (
      (!patientFilter || patient.includes(patientFilter.toLowerCase())) &&
      (!payerFilter || payer.includes(payerFilter.toLowerCase())) &&
      (!providerFilter || provider.includes(providerFilter.toLowerCase())) &&
      (!search ||
        patient.includes(search) ||
        (claim.id ?? '').toLowerCase().includes(search) ||
        getCpts(claim).includes(search))
    );
  });

  const pageClaims = filteredClaims.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <Modal
        opened={Boolean(acknowledgingClaim)}
        onClose={() => setAcknowledgingClaim(undefined)}
        title="Mark claim submitted"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Confirm this claim was submitted to Stedi. This records the handoff only; it does not transmit a claim.
          </Text>
          {acknowledgingClaim &&
            enriched.hasError.has(getReferenceString(acknowledgingClaim as Claim & { id: string })) && (
              <Alert color="yellow" title="Open validation issues">
                Resolve validation issues before submitting when possible. This claim still has an open review task.
              </Alert>
            )}
          <TextInput
            label="Submitted on"
            type="date"
            value={submittedOn}
            onChange={(event) => setSubmittedOn(event.currentTarget.value)}
            required
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAcknowledgingClaim(undefined)} disabled={acknowledging}>
              Cancel
            </Button>
            <Button color="polaris" loading={acknowledging} onClick={acknowledgeClaim}>
              Mark submitted
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Stack gap="md" p="md" className={classes.root}>
        <Group justify="space-between" className={classes.summary}>
          <Text className={classes.summaryTitle}>Claims</Text>
          <Group gap="lg">
            <div className={classes.stat}>
              <Text className={classes.statLabel}>Drafts</Text>
              <Text className={classes.statValue}>{filteredClaims.length}</Text>
            </div>
            <div className={classes.stat}>
              <Text className={classes.statLabel}>Awaiting response</Text>
              <Text className={classes.statValue}>0</Text>
            </div>
            <div className={classes.stat}>
              <Text className={classes.statLabel}>Open tasks</Text>
              <Text className={classes.statValue}>{enriched.hasError.size}</Text>
            </div>
          </Group>
        </Group>

        <Box className={classes.filters}>
          <Group gap="sm" align="flex-end">
            <Select
              label="Status"
              value={statusFilter}
              onChange={(value) => setStatusFilter((value as 'all' | 'draft' | 'active') ?? 'all')}
              data={[
                { value: 'all', label: 'All Statuses' },
                { value: 'draft', label: 'Draft' },
                { value: 'active', label: 'Submitted' },
              ]}
              w={140}
            />
            <Select
              label="Payer outcome"
              value={payerOutcomeFilter}
              onChange={(value) =>
                setPayerOutcomeFilter((value as 'all' | 'none' | 'complete' | 'error' | 'partial') ?? 'all')
              }
              data={[
                { value: 'all', label: 'All' },
                { value: 'none', label: 'No response' },
                { value: 'complete', label: 'Complete' },
                { value: 'partial', label: 'Partial' },
                { value: 'error', label: 'Error' },
              ]}
              w={140}
            />
            <Select
              label="Open task"
              value={openTaskFilter}
              onChange={(value) => setOpenTaskFilter((value as 'all' | 'open' | 'none') ?? 'all')}
              data={[
                { value: 'all', label: 'All' },
                { value: 'open', label: 'Open' },
                { value: 'none', label: 'None' },
              ]}
              w={110}
            />
            <TextInput
              label="Patient"
              placeholder="Filter by patient"
              value={patientFilter}
              onChange={(event) => setPatientFilter(event.currentTarget.value)}
              w={190}
            />
            <TextInput
              label="Payor"
              placeholder="Name or payer ID"
              value={payerFilter}
              onChange={(event) => setPayerFilter(event.currentTarget.value)}
              w={190}
            />
            <TextInput
              label="Provider"
              placeholder="Filter by provider"
              value={providerFilter}
              onChange={(event) => setProviderFilter(event.currentTarget.value)}
              w={190}
            />
          </Group>
          <TextInput
            label="Search"
            placeholder="Claim ID, charge ID, CPT, encounter type"
            value={searchFilter}
            onChange={(event) => setSearchFilter(event.currentTarget.value)}
            mt="sm"
          />
        </Box>

        <Box className={classes.section}>
          <Group justify="space-between" align="flex-start" className={classes.sectionHead}>
            <div>
              <Title order={4}>All claims</Title>
              <Text size="sm" c="dimmed">
                Claims are worked in Stedi; payer outcome lands here from the 277CA and 835. The one thing recorded by
                hand is <strong>Mark submitted</strong>, once a draft has actually gone to the payer. Open task issues
                are for coordinator <code>Task</code> resources.
              </Text>
            </div>
            <Text size="sm" c="dimmed">
              {filteredClaims.length} claim{filteredClaims.length === 1 ? '' : 's'}
            </Text>
          </Group>
          <DataTable
            fetching={loading}
            minHeight={200}
            records={pageClaims}
            idAccessor="id"
            striped
            highlightOnHover
            fz="sm"
            noRecordsText="No claims match this queue."
            totalRecords={filteredClaims.length}
            recordsPerPage={PAGE_SIZE}
            page={page}
            onPageChange={setPage}
            withTableBorder
            borderRadius="md"
            horizontalSpacing="md"
            verticalAlign="center"
            classNames={{
              root: classes.tableRoot,
              table: classes.table,
              header: classes.tableHeader,
              pagination: classes.pagination,
            }}
            columns={[
              {
                accessor: 'created',
                title: 'Created',
                render: (claim) => (claim.created ? formatDate(claim.created) : '—'),
              },
              {
                accessor: 'id',
                title: 'Claim ID',
                render: (claim) => <Text fw={600}>{claim.id ?? '—'}</Text>,
              },
              {
                accessor: 'status',
                title: 'Status',
                render: (claim) => (
                  <Tooltip
                    label={
                      claim.status === 'active'
                        ? 'Submitted to Stedi; payer response is tracked separately.'
                        : 'Draft claim awaiting manual submission to Stedi.'
                    }
                    withArrow
                  >
                    <span>
                      <Badge color={claim.status === 'active' ? 'green' : 'gray'} variant="light">
                        {claim.status === 'active' ? 'Submitted' : 'Draft'}
                      </Badge>
                    </span>
                  </Tooltip>
                ),
              },
              {
                accessor: 'payerOutcome',
                title: 'Payer outcome',
                render: (claim) => (
                  <Text c={enriched.payerOutcome[`Claim/${claim.id}`] ? undefined : 'dimmed'}>
                    {enriched.payerOutcome[`Claim/${claim.id}`] ?? 'No response yet'}
                  </Text>
                ),
              },
              {
                accessor: 'openTask',
                title: 'Open task',
                render: (claim) => {
                  const claimRef = claim.id ? getReferenceString(claim as Claim & { id: string }) : '';
                  const hasError = enriched.hasError.has(claimRef);
                  if (!hasError) {
                    return <Text c="dimmed">—</Text>;
                  }
                  return (
                    <Tooltip
                      label={enriched.taskIssueSummary[claimRef] ?? 'Validation issue needs attention.'}
                      multiline
                      w={280}
                      withArrow
                    >
                      <div className={classes.taskCell} aria-label="Validation issues">
                        <Text c="red" fw={600}>
                          Validation issues
                        </Text>
                        <Text c="dimmed" size="xs">
                          {enriched.taskIssueCount[claimRef] ?? 1}{' '}
                          {enriched.taskIssueCount[claimRef] === 1 ? 'issue' : 'issues'}
                        </Text>
                      </div>
                    </Tooltip>
                  );
                },
              },
              {
                accessor: 'patient',
                title: 'Patient',
                render: (claim) => (
                  <TableAnchor
                    component={Link}
                    to={`/Patient/${getPatientFhirId(claim)}`}
                    size="sm"
                    className={classes.actionLink}
                  >
                    {getPatientName(claim)}
                  </TableAnchor>
                ),
              },
              {
                accessor: 'provider',
                title: 'Provider',
                render: (claim) => getProviderName(claim),
              },
              { accessor: 'encounterType', title: 'Encounter type', render: (claim) => getService(claim) },
              {
                accessor: 'serviceDate',
                title: 'Date of service',
                render: (claim) =>
                  getEncounter(claim)?.period?.start ? formatDate(getEncounter(claim)?.period?.start as string) : '—',
              },
              { accessor: 'payor', title: 'Payor', render: (claim) => getPayerName(claim) },
              { accessor: 'payerId', title: 'Payer ID', render: (claim) => getPayerId(claim) },
              {
                accessor: 'item',
                title: 'CPTs',
                render: (claim) => getCpts(claim),
              },
              { accessor: 'pos', title: 'POS', render: (claim) => getPlaceOfService(claim) },
              {
                accessor: 'total',
                title: 'Billed',
                render: (claim) => getClaimTotal(claim),
              },
              {
                accessor: 'received',
                title: 'Received',
                render: (claim) => {
                  const amount = enriched.receivedAmount[`Claim/${claim.id}`];
                  return amount == null ? '—' : `$${amount.toFixed(2)}`;
                },
              },
              {
                accessor: 'chargeId',
                title: 'Charge ID',
                render: (claim) => claim.identifier?.[0]?.value ?? '—',
              },
              {
                accessor: 'patientAmount',
                title: 'Patient amt.',
                render: (claim) => {
                  const amount = enriched.patientAmt[`Claim/${claim.id}`];
                  return amount == null ? '—' : `$${amount.toFixed(2)}`;
                },
              },
              {
                accessor: 'patientStatus',
                title: 'Patient status',
                render: (claim) => (enriched.patientAmt[`Claim/${claim.id}`] > 0 ? 'Paid' : 'Not paid'),
              },
              {
                accessor: 'actions',
                title: 'Actions',
                render: (claim) => (
                  <Group gap="xs" wrap="nowrap">
                    <TableAnchor component={Link} to={`/claims/${claim.id}`} size="sm" className={classes.actionLink}>
                      {claim.status === 'active' ? 'View claim' : 'Edit draft'}
                    </TableAnchor>
                    {claim.status === 'draft' && (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="polaris"
                        onClick={() => {
                          setSubmittedOn(new Date().toISOString().slice(0, 10));
                          setAcknowledgingClaim(claim);
                        }}
                      >
                        Mark submitted
                      </Button>
                    )}
                  </Group>
                ),
              },
            ]}
          />
        </Box>
      </Stack>
    </>
  );
}
